// src/pages/dashboard/DashboardLPM.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield,
  FileText,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Eye,
  Download,
  Building2,
  BarChart3,
  Clock,
  Hourglass,
  ChevronRight,
  PieChart as PieChartIcon,
  Activity,
  Target,
  AlertCircle,
} from 'lucide-react';
import { Card, Badge, Breadcrumb, ProgressBar, Select, Loading,
} from '../../components/ui';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import api from '../../services/api';
import { TingkatRisiko } from '../../types';

// ========== CONSTANTS ==========
const STATUS_COLORS: Record<string, string> = {
  draf: '#94A3B8',
  diajukan: '#3B82F6',
  validasi_l1: '#06B6D4',
  validasi_l2: '#A855F7',
  antrean_rapim: '#F97316',
  berlangsung: '#EAB308',
  menunggu_konfirmasi: '#F59E0B',
  selesai: '#10B981',
  ditolak_l1: '#EF4444',
  ditolak_l2: '#DC2626',
};

const RISK_COLORS: Record<string, string> = {
  rendah: '#22C55E',
  sedang: '#F59E0B',
  tinggi: '#F97316',
  kritis: '#EF4444',
};

const KATEGORI_COLORS = [
  '#0f766e', '#14B8A6', '#3B82F6', '#8B5CF6',
  '#F59E0B', '#EF4444', '#06B6D4', '#22C55E',
];

// ========== TYPES ==========
interface DashboardData {
  total_laporan: number;
  laporan_per_status: Record<string, number>;
  laporan_overdue: number;
  rata_rata_penyelesaian: number;
  laporan_per_risiko: Record<string, number>;
  progress_ppepp: number;
  tren_bulanan: Array<{ bulan: string; jumlah: number; selesai: number }>;
  laporan_per_kategori: Array<{ kategori: string; jumlah: number; color?: string }>;
  overdue_list: Array<{
    kode: string;
    judul: string;
    unit: string;
    risiko: string;
    hari: number;
    hari_terlambat?: number;
  }>;
}

interface UnitPerformance {
  unit_id?: number;
  unit: string;
  total: number;
  selesai: number;
  overdue: number;
  avg_days?: number;
  completion_rate?: number;
}

interface Periode {
  id: number;
  nama: string;
  is_active?: boolean;
}

// ========== HELPER FUNCTIONS ==========
const toRiskLevel = (risk: string | undefined): TingkatRisiko => {
  const validRisks: TingkatRisiko[] = ['rendah', 'sedang', 'tinggi', 'kritis'];
  const normalized = (risk || 'sedang').toLowerCase() as TingkatRisiko;
  return validRisks.includes(normalized) ? normalized : 'sedang';
};

const getStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    draf: 'Draft',
    diajukan: 'Diajukan',
    validasi_l1: 'Validasi Dekan',
    validasi_l2: 'Validasi Warek',
    ditolak_l1: 'Ditolak L1',
    ditolak_l2: 'Ditolak L2',
    antrean_rapim: 'Antrean RAPIM',
    diputuskan: 'Diputuskan',
    berlangsung: 'Dalam Proses',
    menunggu_konfirmasi: 'Menunggu Konfirmasi',
    selesai: 'Selesai',
    diarsipkan: 'Diarsipkan',
  };
  return labels[status] || status;
};

// ========== COMPONENT ==========
const DashboardLPM: React.FC = () => {
  // States
  const [loading, setLoading] = useState(true);
  const [dashData, setDashData] = useState<DashboardData | null>(null);
  const [unitPerformance, setUnitPerformance] = useState<UnitPerformance[]>([]);
  const [periodeList, setPeriodeList] = useState<Periode[]>([]);
  const [selectedPeriode, setSelectedPeriode] = useState<string>('');
  const [isPeriodeReady, setIsPeriodeReady] = useState(false);

  useEffect(() => {
    const fetchPeriode = async () => {
      try {
        const periodeRes = await api.get('/admin/periode').catch(() => ({ data: { data: [] } }));
        const periodeData = periodeRes.data?.data || periodeRes.data || [];
        const normalizedPeriode = Array.isArray(periodeData) ? periodeData : [];
        setPeriodeList(normalizedPeriode);

        const activePeriode = normalizedPeriode.find((p: Periode) => p.is_active);
        setSelectedPeriode(activePeriode ? String(activePeriode.id) : '');
      } catch {
        setPeriodeList([]);
        setSelectedPeriode('');
      } finally {
        setIsPeriodeReady(true);
      }
    };

    fetchPeriode();
  }, []);

  // ========== FETCH DATA ==========
  useEffect(() => {
    if (!isPeriodeReady) {
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        const params: Record<string, any> = { context: 'monitoring' };
        if (selectedPeriode) params.periode_id = selectedPeriode;

        const [dashRes, unitRes] = await Promise.all([
          api.get('/dashboard', { params }),
          api.get('/monitoring/unit-performance', { params }).catch(() => ({ data: { data: [] } })),
        ]);

        // Parse dashboard data
        const d = dashRes.data?.data || dashRes.data;
        if (d) {
          setDashData({
            total_laporan: d.total_laporan || 0,
            laporan_per_status: d.laporan_per_status || {},
            laporan_overdue: d.laporan_overdue || 0,
            rata_rata_penyelesaian: d.rata_rata_penyelesaian || 0,
            laporan_per_risiko: d.laporan_per_risiko || {},
            progress_ppepp: d.progress_ppepp || 0,
            tren_bulanan: d.tren_bulanan || [],
            laporan_per_kategori: d.laporan_per_kategori || [],
            overdue_list: d.overdue_list || [],
          });
        }

        // Parse unit performance
        const unitData = unitRes.data?.data || unitRes.data;
        setUnitPerformance(Array.isArray(unitData) ? unitData : []);

      } catch (err) {
        console.error('Failed to load LPM dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedPeriode, isPeriodeReady]);

  // ========== COMPUTED DATA ==========

  // Status pie data - exclude draft dan arsip untuk fokus pada yang aktif
  const statusPieData = Object.entries(dashData?.laporan_per_status || {})
    .filter(([key]) => !['draf', 'diarsipkan', 'tugas_ppepp_aktif'].includes(key))
    .filter(([, value]) => value > 0)
    .map(([key, value]) => ({
      name: getStatusLabel(key),
      value,
      fill: STATUS_COLORS[key] || '#9CA3AF',
    }));

  // Risk pie data
  const riskPieData = Object.entries(dashData?.laporan_per_risiko || {})
    .filter(([, value]) => value > 0)
    .map(([key, value]) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      value,
      fill: RISK_COLORS[key] || '#9CA3AF',
    }));

  // Kategori chart data
  const kategoriChartData = (dashData?.laporan_per_kategori || []).map((item, idx) => ({
    name: item.kategori,
    jumlah: item.jumlah,
    fill: item.color || KATEGORI_COLORS[idx % KATEGORI_COLORS.length],
  }));

  // KPI calculations
  const totalLaporan = dashData?.total_laporan || 0;
  const totalSelesaiRaw = dashData?.laporan_per_status?.selesai || 0;
  const totalDiarsipkan = dashData?.laporan_per_status?.diarsipkan || 0;
  const totalSelesai = totalSelesaiRaw + totalDiarsipkan;
  const totalOverdue = dashData?.laporan_overdue || 0;
  const menungguKonfirmasi = dashData?.laporan_per_status?.menunggu_konfirmasi || 0;
  const completionRate = totalLaporan > 0 ? Math.round((totalSelesai / totalLaporan) * 100) : 0;
  const progressPpepp = dashData?.progress_ppepp || 0;
  const rataRataPenyelesaian = dashData?.rata_rata_penyelesaian || 0;

  // In progress count
  const inProgressStatuses = ['diajukan', 'validasi_l1', 'validasi_l2', 'antrean_rapim', 'berlangsung'];
  const inProgressCount = Object.entries(dashData?.laporan_per_status || {})
    .filter(([k]) => inProgressStatuses.includes(k))
    .reduce((acc, [, v]) => acc + v, 0);

  // Top overdue
  const topOverdue = (dashData?.overdue_list || []).slice(0, 5);

  // Unit performance sorted by completion rate
  const sortedUnitPerformance = [...unitPerformance]
    .map((u) => ({
      ...u,
      completion_rate: u.total > 0 ? Math.round((u.selesai / u.total) * 100) : 0,
    }))
    .sort((a, b) => b.completion_rate - a.completion_rate);

  // Periode options
  const periodeOptions = [
    { value: '', label: 'Semua Periode' },
    ...periodeList.map((p) => ({
      value: String(p.id),
      label: `${p.nama}${p.is_active ? ' (Aktif)' : ''}`,
    })),
  ];

  // ========== LOADING STATE ==========
  if (loading) {
    return <Loading variant="page" message="Memuat data dashboard..." />;
  }

  // ========== RENDER ==========
  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Dashboard' }, { label: 'LPM' }]} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">
            Dashboard LPM
          </h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            <Shield className="w-4 h-4 inline mr-1" />
            Monitoring & Audit Mutu Internal (Observer Mode)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            options={periodeOptions}
            value={selectedPeriode}
            onChange={setSelectedPeriode}
            className="w-full sm:w-56"
          />
          <Link to="/reports?action=export">
            <button className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-neutral-border text-sm font-semibold rounded-lg hover:bg-neutral-bg transition-all">
              <Download className="w-4 h-4" /> Export Data
            </button>
          </Link>
        </div>
      </div>

      {/* Observer Mode Banner */}
      <div className="p-3 bg-info/5 border border-info/20 rounded-xl flex items-center gap-3">
        <Eye className="w-5 h-5 text-info shrink-0" />
        <p className="text-sm text-info">
          <span className="font-semibold">Mode Observer:</span> Anda memiliki akses
          baca ke seluruh data evaluasi mutu tanpa dapat melakukan perubahan.
        </p>
      </div>

      {/* Alert: Overdue & Menunggu Konfirmasi */}
      {(totalOverdue > 0 || menungguKonfirmasi > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {totalOverdue > 0 && (
            <div className="p-3 bg-danger/5 border border-danger/20 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-danger shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-danger">
                  {totalOverdue} laporan melewati deadline
                </p>
              </div>
              <Link
                to="/laporan?status=overdue"
                className="text-xs font-semibold text-danger hover:underline shrink-0"
              >
                Lihat →
              </Link>
            </div>
          )}
          {menungguKonfirmasi > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
              <Hourglass className="w-5 h-5 text-amber-600 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-700">
                  {menungguKonfirmasi} menunggu konfirmasi pimpinan
                </p>
              </div>
              <Link
                to="/laporan?status=menunggu_konfirmasi"
                className="text-xs font-semibold text-amber-700 hover:underline shrink-0"
              >
                Lihat →
              </Link>
            </div>
          )}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          {
            title: 'Total Laporan',
            value: totalLaporan,
            icon: <FileText className="w-5 h-5" />,
            color: 'text-teal-main',
            bgIcon: 'bg-teal-main/10',
          },
          {
            title: 'Dalam Proses',
            value: inProgressCount,
            icon: <Clock className="w-5 h-5" />,
            color: 'text-warning',
            bgIcon: 'bg-warning/10',
          },
          {
            title: 'Konfirmasi',
            value: menungguKonfirmasi,
            icon: <Hourglass className="w-5 h-5" />,
            color: 'text-amber-600',
            bgIcon: 'bg-amber-100',
          },
          {
            title: 'Selesai',
            value: totalSelesai,
            icon: <CheckCircle className="w-5 h-5" />,
            color: 'text-success',
            bgIcon: 'bg-success/10',
          },
          {
            title: 'Terlambat',
            value: totalOverdue,
            icon: <AlertTriangle className="w-5 h-5" />,
            color: 'text-danger',
            bgIcon: 'bg-danger/10',
          },
          {
            title: 'Completion Rate',
            value: `${completionRate}%`,
            icon: <TrendingUp className="w-5 h-5" />,
            color: 'text-info',
            bgIcon: 'bg-info/10',
          },
        ].map((kpi, i) => (
          <Card key={i}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                  {kpi.title}
                </p>
                <p className={`text-2xl font-black mt-1 ${kpi.color}`}>
                  {kpi.value}
                </p>
              </div>
              <div className={`p-2 rounded-lg ${kpi.bgIcon} ${kpi.color}`}>
                {kpi.icon}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Additional KPIs Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-purple-100">
              <Activity className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Progress PPEPP
              </p>
              <p className="text-2xl font-black text-purple-600">{progressPpepp}%</p>
            </div>
          </div>
          <div className="mt-3">
            <ProgressBar value={progressPpepp} color="teal" size="sm" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-info/10">
              <Target className="w-6 h-6 text-info" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Rata-rata Penyelesaian
              </p>
              <p className="text-2xl font-black text-info">{rataRataPenyelesaian} hari</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-success/10">
              <Building2 className="w-6 h-6 text-success" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Unit Terpantau
              </p>
              <p className="text-2xl font-black text-success">{unitPerformance.length}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Charts Row 1: Status Distribution + Risk Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
              <PieChartIcon className="w-5 h-5" /> Distribusi Status Laporan
            </h3>
            <Link
              to="/laporan"
              className="text-xs text-teal-main hover:underline flex items-center gap-1"
            >
              Detail <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          {statusPieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={statusPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {statusPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} laporan`, '']} />
                <Legend
                  verticalAlign="bottom"
                  iconSize={8}
                  formatter={(value: string) => (
                    <span className="text-[10px] text-neutral-text-secondary">{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-neutral-text-secondary">
              Belum ada data status
            </div>
          )}
        </Card>

        {/* Risk Distribution */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Distribusi Tingkat Risiko
            </h3>
          </div>
          {riskPieData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={riskPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {riskPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} laporan`, '']} />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {riskPieData.map((item, i) => {
                  const percentage =
                    totalLaporan > 0 ? Math.round((item.value / totalLaporan) * 100) : 0;
                  return (
                    <div key={i} className="flex items-center justify-between p-2 bg-neutral-bg rounded-lg">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: item.fill }}
                        />
                        <span className="text-xs font-medium">{item.name}</span>
                      </div>
                      <span className="text-xs font-bold">
                        {item.value} ({percentage}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-neutral-text-secondary">
              Belum ada data risiko
            </div>
          )}
        </Card>
      </div>

      {/* Charts Row 2: Trend + Kategori */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trend Bulanan */}
        <Card>
          <h3 className="text-base font-bold text-teal-dark mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" /> Tren Laporan 6 Bulan Terakhir
          </h3>
          {(dashData?.tren_bulanan || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={dashData?.tren_bulanan}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="bulan" tick={{ fontSize: 11, fill: '#6B7280' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    fontSize: '11px',
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Line
                  type="monotone"
                  dataKey="jumlah"
                  stroke="#0f33ab"
                  strokeWidth={2.5}
                  dot={{ fill: '#0f33ab', r: 4 }}
                  name="Laporan Masuk"
                />
                <Line
                  type="monotone"
                  dataKey="selesai"
                  stroke="#22C55E"
                  strokeWidth={2}
                  dot={{ fill: '#22C55E', r: 3 }}
                  name="Selesai"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-neutral-text-secondary">
              Belum ada data tren
            </div>
          )}
        </Card>

        {/* Laporan per Kategori */}
        <Card>
          <h3 className="text-base font-bold text-teal-dark mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" /> Laporan per Kategori
          </h3>
          {kategoriChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={kategoriChartData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={100}
                  tick={{ fontSize: 10 }}
                />
                <Tooltip formatter={(value) => [`${value} laporan`, '']} />
                <Bar dataKey="jumlah" radius={[0, 6, 6, 0]}>
                  {kategoriChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-neutral-text-secondary">
              Belum ada data kategori
            </div>
          )}
        </Card>
      </div>

      {/* Overdue List + Unit Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Overdue List */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-danger" /> Laporan Terlambat
            </h3>
            {totalOverdue > 5 && (
              <Link
                to="/laporan?status=overdue"
                className="text-xs text-teal-main hover:underline flex items-center gap-1"
              >
                Lihat Semua ({totalOverdue}) <ChevronRight className="w-3 h-3" />
              </Link>
            )}
          </div>
          <div className="space-y-2">
            {topOverdue.length > 0 ? (
              topOverdue.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 bg-danger/5 rounded-xl border border-danger/10"
                >
                  <div className="text-center shrink-0 w-12">
                    <p className="text-lg font-black text-danger leading-none">
                      {Math.abs(item.hari_terlambat || item.hari || 0)}
                    </p>
                    <p className="text-[9px] text-danger font-semibold">HARI</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-mono text-teal-main">{item.kode}</span>
                      <Badge variant="risk" risk={toRiskLevel(item.risiko)} />
                    </div>
                    <p className="text-sm font-medium truncate">{item.judul}</p>
                    <p className="text-xs text-neutral-text-secondary">{item.unit}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 text-success" />
                <p className="text-sm font-semibold text-success">Tidak ada laporan terlambat!</p>
                <p className="text-xs text-neutral-text-secondary mt-1">
                  Semua laporan dalam target waktu
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Unit Performance */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
              <Building2 className="w-5 h-5" /> Kinerja per Unit
            </h3>
          </div>
          <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
            {sortedUnitPerformance.length > 0 ? (
              sortedUnitPerformance.map((u, i) => (
                <div key={i} className="p-3 bg-neutral-bg rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-bold truncate flex-1">{u.unit}</p>
                    <span
                      className={`text-sm font-bold ${u.completion_rate! >= 80
                          ? 'text-success'
                          : u.completion_rate! >= 50
                            ? 'text-warning'
                            : 'text-danger'
                        }`}
                    >
                      {u.completion_rate}%
                    </span>
                  </div>
                  <ProgressBar
                    value={u.completion_rate || 0}
                    color={
                      u.completion_rate! >= 80
                        ? 'success'
                        : u.completion_rate! >= 50
                          ? 'warning'
                          : 'danger'
                    }
                    size="sm"
                    showLabel={false}
                  />
                  <div className="flex items-center gap-4 mt-2 text-xs text-neutral-text-secondary">
                    <span>Total: {u.total}</span>
                    <span className="text-success">Selesai: {u.selesai}</span>
                    {u.overdue > 0 && (
                      <span className="text-danger">Terlambat: {u.overdue}</span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-neutral-text-secondary text-center py-4">
                Belum ada data kinerja unit
              </p>
            )}
          </div>
        </Card>
      </div>

      {/* Unit Performance Bar Chart */}
      {sortedUnitPerformance.length > 0 && (
        <Card>
          <h3 className="text-base font-bold text-teal-dark mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" /> Perbandingan Kinerja Unit
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={sortedUnitPerformance.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="unit"
                tick={{ fontSize: 10 }}
                interval={0}
                angle={-20}
                textAnchor="end"
                height={60}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="selesai" name="Selesai" fill="#22C55E" radius={[4, 4, 0, 0]} />
              <Bar dataKey="overdue" name="Terlambat" fill="#EF4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Quick Links for Observer */}
      <Card>
        <h3 className="text-base font-bold text-teal-dark mb-4">Akses Cepat Audit</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Link to="/laporan">
            <div className="p-4 bg-neutral-bg rounded-xl hover:bg-teal-main/5 hover:border-teal-main/20 border border-transparent transition-all text-center">
              <FileText className="w-6 h-6 mx-auto mb-2 text-teal-main" />
              <p className="text-xs font-semibold">Semua Laporan</p>
            </div>
          </Link>
          <Link to="/monitoring">
            <div className="p-4 bg-neutral-bg rounded-xl hover:bg-teal-main/5 hover:border-teal-main/20 border border-transparent transition-all text-center">
              <Activity className="w-6 h-6 mx-auto mb-2 text-info" />
              <p className="text-xs font-semibold">Monitoring</p>
            </div>
          </Link>
          <Link to="/reports">
            <div className="p-4 bg-neutral-bg rounded-xl hover:bg-teal-main/5 hover:border-teal-main/20 border border-transparent transition-all text-center">
              <BarChart3 className="w-6 h-6 mx-auto mb-2 text-purple-600" />
              <p className="text-xs font-semibold">Laporan & Analitik</p>
            </div>
          </Link>
          <Link to="/rapim">
            <div className="p-4 bg-neutral-bg rounded-xl hover:bg-teal-main/5 hover:border-teal-main/20 border border-transparent transition-all text-center">
              <Shield className="w-6 h-6 mx-auto mb-2 text-warning" />
              <p className="text-xs font-semibold">Riwayat RAPIM</p>
            </div>
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default DashboardLPM;
